// The sounds of war, period by period, rendered once in the browser from small physical models (dsp.ts): nothing is
// recorded or downloaded. Each recipe renders a few variants so repeats differ (a volley is never one shot copied).
// Which period uses which sound is decided in score.ts.

import {
  between, Biquad, buffer, burst, fadeOut, hump, loopable, mode, noise, normalize, OnePole, Osc, panGains, pick, rng,
  smoothstep, type Rng,
} from "./dsp";

export interface Recipe {
  sr: number;
  variants: number;
  loop?: boolean; // made to loop without a seam (fire, engines, rotors)
  heavy?: boolean; // slow to make (a tenth of a second or more): only ever made in the worker
  render(r: Rng, v: number, sr: number): Float32Array[]; // one channel, or two for wide sounds (crowds, cavalry)
}

const TAU = 2 * Math.PI;

/** Add `src` into channels at `at` seconds with gains (one per channel). */
function mix(chs: Float32Array[], src: Float32Array, sr: number, at: number, gains: number[]) {
  const i0 = Math.round(at * sr);
  chs.forEach((c, k) => {
    const g = gains[k] ?? gains[0];
    for (let i = 0; i < src.length && i0 + i < c.length; i++) if (i0 + i >= 0) c[i0 + i] += src[i] * g;
  });
}

/** A late, duller copy of the sound: the land answering. */
function echo(out: Float32Array, sr: number, delay: number, g: number, f: number) {
  const d = Math.round(delay * sr), lp = new Biquad(sr, "lowpass", f, 0.7), e = new Float32Array(out.length);
  for (let i = d; i < out.length; i++) e[i] = lp.run(out[i - d]) * g;
  for (let i = 0; i < out.length; i++) out[i] += e[i];
}

/** A low thump falling in pitch (cannon, explosions, a stone striking a wall). */
function thump(out: Float32Array, sr: number, at: number, f0: number, f1: number, bend: number, tau: number, a: number) {
  const i0 = Math.round(at * sr);
  let ph = 0;
  for (let i = i0; i < out.length; i++) {
    const t = (i - i0) / sr, f = f1 + (f0 - f1) * Math.exp(-t / bend);
    ph += (TAU * f) / sr;
    out[i] += a * Math.sin(ph) * Math.exp(-t / tau) * Math.min(1, t / 0.003);
  }
}

/** A short rising chirp (a drop, a bubble). */
function chirp(out: Float32Array, sr: number, at: number, f0: number, f1: number, d: number, a: number) {
  const i0 = Math.round(at * sr), n = Math.round(d * sr);
  let ph = 0;
  for (let i = 0; i < n && i0 + i < out.length; i++) {
    const k = i / n;
    ph += (TAU * (f0 + (f1 - f0) * k)) / sr;
    out[i0 + i] += a * Math.sin(ph) * Math.sin(Math.PI * k);
  }
}

/** Poisson moments from `from` to `to` at a rate (per second) that may change with time. */
function moments(r: Rng, from: number, to: number, rate: (t: number) => number): number[] {
  const out: number[] = [];
  for (let t = from; ;) {
    t += -Math.log(1 - r()) / Math.max(0.01, rate(t));
    if (t >= to) return out;
    out.push(t);
  }
}

// --- firearms ---------------------------------------------------------------------------------------

interface Shot {
  len: number;
  crack: number; // the bullet's supersonic crack, heard before the report downrange (0: none)
  lead: [number, number]; // how much earlier (s)
  tau: number; // the report's decay
  bp: [number, number]; // the report's band (centre, a range)
  q: number;
  lp: number;
  body: [number, number, number]; // thump: Hz, level, decay
  tail: [number, number]; // the rumble after: level, decay
  echo: number;
  flash?: number; // flintlock: the priming pan's hiss before the charge goes off
  clank?: number; // a machine gun's working parts
}

function shot(p: Shot): Recipe["render"] {
  return (r, v, sr) => {
    const out = buffer(sr, p.len), white = noise(r);
    let t0 = 0.002;
    if (p.flash && v % 2 === 0) {
      mode(out, sr, 0.001, between(r, 2400, 3400), 0.25, 0.004); // flint on steel
      burst(out, sr, 0.004, p.flash, 0.012, white, new Biquad(sr, "bandpass", between(r, 2800, 4200), 0.9), 0.003); // the pan
      t0 = between(r, 0.03, 0.06);
    }
    if (p.crack > 0 && v % 3 !== 2) {
      const nw = Math.max(2, Math.round(0.00045 * sr)), i0 = Math.round(0.001 * sr); // an N-wave
      for (let i = 0; i < nw; i++) out[i0 + i] += p.crack * (i < nw / 2 ? 1 : -1);
      t0 = 0.001 + between(r, p.lead[0], p.lead[1]);
    }
    const rep = buffer(sr, p.len);
    const bp = new Biquad(sr, "bandpass", between(r, p.bp[0], p.bp[1]), p.q), lp = new Biquad(sr, "lowpass", p.lp, 0.7);
    burst(rep, sr, t0, 1, p.tau, white, { run: (x) => lp.run(bp.run(x) * 1.6 + x * 0.35) });
    burst(rep, sr, t0, 0.6, 0.0012, white); // a millisecond of raw blast: the punch
    mode(rep, sr, t0, p.body[0] * between(r, 0.9, 1.1), p.body[1], p.body[2]);
    burst(rep, sr, t0 + 0.003, p.tail[0], p.tail[1], white, new Biquad(sr, "lowpass", 700, 0.7), 0.006);
    if (p.clank) mode(rep, sr, t0 + between(r, 0.025, 0.04), between(r, 1800, 2600), p.clank, 0.005);
    const d = Math.round(between(r, 0.012, 0.03) * sr); // off the ground
    for (let i = 0; i < out.length; i++) out[i] += rep[i] + (i >= d ? rep[i - d] * p.echo : 0);
    fadeOut(out, sr, 0.05);
    return normalize([out], 0.95);
  };
}

// --- heavy guns and explosions -------------------------------------------------------------------------

function cannon(r: Rng, _v: number, sr: number): Float32Array[] {
  const out = buffer(sr, 3.2), white = noise(r), brown = noise(r, "brown");
  thump(out, sr, 0, between(r, 46, 58), 26, 0.25, 0.42, 1);
  const lp = new Biquad(sr, "lowpass", 2400, 0.6); // black powder: a great dull blast closing down to a rumble
  for (let i = 0; i < out.length; i++) {
    const t = i / sr;
    if (i % 32 === 0) lp.set("lowpass", 120 + 2300 * Math.exp(-t / 0.35), 0.6);
    out[i] += lp.run(white() * 0.6 + brown() * 0.8) * Math.min(1, t / 0.003) * Math.exp(-t / 0.55) * 0.9;
  }
  burst(out, sr, 0.0005, 0.5, 0.006, white, new Biquad(sr, "highpass", 600, 0.7));
  echo(out, sr, between(r, 0.35, 0.6), 0.22, 600);
  echo(out, sr, between(r, 0.9, 1.3), 0.12, 400);
  fadeOut(out, sr, 0.25);
  return normalize([out], 0.95);
}

function blast(r: Rng, _v: number, sr: number): Float32Array[] {
  const out = buffer(sr, 3.4), white = noise(r), pink = noise(r, "pink"), brown = noise(r, "brown");
  burst(out, sr, 0.001, 0.9, 0.005, white, new Biquad(sr, "highpass", 1000, 0.7)); // high explosive: a hard crack
  thump(out, sr, 0.001, between(r, 55, 66), 24, 0.2, 0.5, 1);
  const lp = new Biquad(sr, "lowpass", 3500, 0.6);
  for (let i = 0; i < out.length; i++) {
    const t = i / sr;
    if (i % 32 === 0) lp.set("lowpass", 100 + 3400 * Math.exp(-t / 0.4), 0.6);
    out[i] += lp.run(pink() * 0.8 + brown() * 0.7) * Math.min(1, t / 0.002) * Math.exp(-t / 0.7) * 0.9;
  }
  for (const t of moments(r, 0.12, 2.6, (x) => 90 * Math.exp(-x / 0.7) + 4)) { // earth and stones falling back
    burst(out, sr, t, 0.25 * r() ** 2 * Math.exp(-t / 1.2), between(r, 0.002, 0.008), white,
      new Biquad(sr, "bandpass", between(r, 600, 3200), 2));
  }
  echo(out, sr, between(r, 0.5, 0.8), 0.15, 500);
  fadeOut(out, sr, 0.3);
  return normalize([out], 0.95);
}

// --- blades, shields, bows ------------------------------------------------------------------------------

function sword(r: Rng, v: number, sr: number): Float32Array[] {
  const out = buffer(sr, 0.8), white = noise(r), f1 = between(r, 480, 950);
  [1, 2.32, 2.76, 4.1, 5.4, 6.8, 8.93, 11.6].forEach((k, i) => { // a blade's modes: inharmonic, the high ones short
    const f = f1 * k * between(r, 0.98, 1.02), a = between(r, 0.5, 1) / (1 + 0.25 * i), tau = between(r, 0.22, 0.55) / (1 + 0.45 * i);
    mode(out, sr, 0.0015, f, a, tau, r() * TAU);
    mode(out, sr, 0.0015, f * between(r, 1.002, 1.006), a * 0.45, tau, r() * TAU); // a twin a little apart: shimmer
  });
  burst(out, sr, 0, 1.1, 0.0015, white, new Biquad(sr, "highpass", 2500, 0.7)); // the strike
  if (v % 3 === 2) { // one blade sliding off the other
    const bp = new Biquad(sr, "bandpass", 2200, 6), i0 = Math.round(0.01 * sr), n = Math.round(0.2 * sr);
    for (let i = 0; i < n; i++) {
      const t = i / sr;
      if (i % 16 === 0) bp.set("bandpass", 2200 + 2600 * (t / 0.2), 6);
      out[i0 + i] += bp.run(white()) * 0.4 * hump(t, 0.05, 0.06, 0.5);
    }
  }
  fadeOut(out, sr, 0.05);
  return normalize([out], 0.85);
}

function shield(r: Rng, v: number, sr: number): Float32Array[] {
  const out = buffer(sr, 0.4), white = noise(r), f0 = between(r, 120, 220);
  [1, 1.52, 2.21, 2.87, 3.6].forEach((k, i) =>
    mode(out, sr, 0.001, f0 * k * between(r, 0.97, 1.03), 1 / (1 + 0.5 * i), between(r, 0.03, 0.07) / (1 + 0.3 * i), r() * TAU));
  burst(out, sr, 0, 0.8, 0.01, white, new Biquad(sr, "lowpass", 800, 0.7), 0.001);
  if (v % 2) mode(out, sr, 0.001, between(r, 1900, 2800), 0.3, 0.05); // the iron boss
  fadeOut(out, sr, 0.03);
  return normalize([out], 0.85);
}

function bow(r: Rng, _v: number, sr: number): Float32Array[] {
  const out = buffer(sr, 0.3), f = between(r, 95, 140);
  for (let h = 1; h <= 8; h++) mode(out, sr, 0.001, f * h, 1 / h ** 1.3, 0.06 / (1 + 0.3 * h)); // the string
  burst(out, sr, 0, 0.5, 0.004, noise(r), new Biquad(sr, "lowpass", 1500, 0.7));
  return normalize([out], 0.7);
}

function arrow(r: Rng, _v: number, sr: number): Float32Array[] {
  const out = buffer(sr, 0.9), white = noise(r), tp = 0.42, fc = between(r, 2400, 4200), q = between(r, 2.5, 5);
  const bp = new Biquad(sr, "bandpass", fc, q), lp = new Biquad(sr, "lowpass", 900, 0.7);
  for (let i = 0; i < out.length; i++) {
    const t = i / sr;
    if (i % 16 === 0) bp.set("bandpass", fc * (1 + 0.16 * Math.tanh((tp - t) / 0.08)), q); // falling as it passes
    const x = white();
    out[i] = (bp.run(x) + lp.run(x) * 0.35) * hump(t, tp, 0.09, 1.8);
  }
  return normalize([out], 0.6);
}

function thud(r: Rng, v: number, sr: number): Float32Array[] {
  const out = buffer(sr, 0.22), white = noise(r);
  if (v % 3 === 0) { // into wood: a shield, a palisade
    const f0 = between(r, 350, 700);
    [1, 1.7, 2.6].forEach((k, i) => mode(out, sr, 0.001, f0 * k, 1 / (1 + i), between(r, 0.02, 0.035)));
    burst(out, sr, 0, 0.6, 0.002, white, new Biquad(sr, "highpass", 1500, 0.7));
  } else if (v % 3 === 1) { // into the ground
    burst(out, sr, 0, 1, 0.012, white, new Biquad(sr, "lowpass", 400, 0.7), 0.001);
    mode(out, sr, 0.001, between(r, 80, 110), 0.5, 0.02);
  } else { // off armour
    mode(out, sr, 0.001, between(r, 2600, 4000), 0.6, 0.05);
    mode(out, sr, 0.001, between(r, 5200, 7000), 0.25, 0.03);
    burst(out, sr, 0, 0.5, 0.0015, white, new Biquad(sr, "highpass", 2000, 0.7));
  }
  return normalize([out], 0.8);
}

// --- horns, bugles, drums ------------------------------------------------------------------------------

interface Note { f: number; d: number }

/** A lip-buzzed brass voice: brighter as it gets louder; tongued (bugle) or slurred (horn) between notes. */
function brass(r: Rng, sr: number, notes: Note[], o: { bright: number; attack: number; legato: number; growl?: number }): Float32Array {
  const total = notes.reduce((s, n) => s + n.d, 0);
  const out = buffer(sr, total + 0.05), osc = new Osc(sr), white = noise(r);
  const lp = new Biquad(sr, "lowpass", 800, 0.9), bell = new Biquad(sr, "peaking", 620, 1.2, 6);
  const top = new Biquad(sr, "lowpass", o.bright > 0.6 ? 6500 : 3600, 0.7), breath = new Biquad(sr, "bandpass", 1200, 0.8);
  let k = 0, start = 0, jit = 0;
  for (let i = 0; i < out.length; i++) {
    const t = i / sr;
    while (k < notes.length - 1 && t >= start + notes[k].d) { start += notes[k].d; k++; }
    const n = notes[k], into = t - start, left = n.d - into, last = k === notes.length - 1;
    const floor = k === 0 ? 0 : o.legato; // a slurred note starts where the last one ended
    let e = floor + (1 - floor) * smoothstep(0, o.attack, into);
    e *= last ? smoothstep(-0.02, 0.28, left) : 1 - (1 - o.legato) * (1 - smoothstep(0, 0.03, left));
    jit = (jit + (r() - 0.5) * 0.0004) * 0.999;
    const f = n.f * (1 - 0.05 * Math.exp(-into / 0.05)) * (1 + 0.006 * Math.sin(TAU * 4.8 * t) * smoothstep(0.3, 0.8, into)) * (1 + jit);
    const [saw, sq] = osc.next(f);
    if (i % 32 === 0) lp.set("lowpass", 250 + 600 * e + o.bright * 4000 * e ** 1.6, 0.9);
    let x = top.run(bell.run(lp.run(saw * 0.75 + sq * 0.25))) + breath.run(white()) * 0.05 * e;
    if (o.growl) x *= 1 - o.growl * (0.5 + 0.5 * Math.sin(TAU * 28 * t));
    out[i] = x * e;
  }
  return out;
}

function horn(r: Rng, v: number, sr: number): Float32Array[] {
  const f0 = between(r, 98, 117);
  const out = v === 0 ? brass(r, sr, [{ f: f0, d: 2.1 }], { bright: 0.35, attack: 0.18, legato: 1 })
    : v === 1 ? brass(r, sr, [{ f: f0, d: 0.9 }, { f: f0 * 1.498, d: 1.25 }], { bright: 0.4, attack: 0.12, legato: 0.6 })
    : brass(r, sr, [{ f: between(r, 140, 165), d: 1.7 }], { bright: 0.55, attack: 0.1, legato: 1, growl: 0.25 }); // a carnyx
  return normalize([out], 0.8);
}

function bugle(r: Rng, v: number, sr: number): Float32Array[] {
  const c = between(r, 0.994, 1.006), [G4, C5, E5, G5] = [392, 523.25, 659.26, 783.99].map((f) => f * c);
  const call: Note[] = v === 0
    ? [{ f: G4, d: 0.13 }, { f: C5, d: 0.13 }, { f: E5, d: 0.13 }, { f: G5, d: 0.26 }, { f: E5, d: 0.13 }, { f: G5, d: 0.6 }] // a charge
    : [{ f: C5, d: 0.28 }, { f: E5, d: 0.14 }, { f: G5, d: 0.14 }, { f: E5, d: 0.28 }, { f: C5, d: 0.28 }, { f: G4, d: 0.55 }];
  return normalize([brass(r, sr, call, { bright: 0.85, attack: 0.025, legato: 0.15 })], 0.75);
}

function wardrum(r: Rng, _v: number, sr: number): Float32Array[] {
  const out = buffer(sr, 1.6), white = noise(r), f0 = between(r, 52, 72);
  let p1 = 0, p2 = 0, p3 = 0;
  for (let i = 0; i < out.length; i++) {
    const t = i / sr, b = 1 + 0.5 * Math.exp(-t / 0.04); // the skin's pitch drops after the blow
    p1 += (TAU * f0 * b) / sr; p2 += (TAU * f0 * 1.59 * b) / sr; p3 += (TAU * f0 * 2.14 * b) / sr;
    out[i] = Math.sin(p1) * Math.exp(-t / 0.4) + 0.35 * Math.sin(p2) * Math.exp(-t / 0.2) + 0.2 * Math.sin(p3) * Math.exp(-t / 0.12);
  }
  burst(out, sr, 0, 0.6, 0.008, white, new Biquad(sr, "lowpass", 900, 0.7), 0.001);
  burst(out, sr, 0, 0.3, 0.02, white, new Biquad(sr, "bandpass", 300, 1));
  return normalize([out], 0.9);
}

function snare(r: Rng, _v: number, sr: number): Float32Array[] {
  const out = buffer(sr, 0.45), white = noise(r), f = between(r, 185, 215);
  let p = 0, p2 = 0;
  for (let i = 0; i < out.length; i++) {
    const t = i / sr, b = 1 + 0.15 * Math.exp(-t / 0.01);
    p += (TAU * f * b) / sr; p2 += (TAU * f * 1.6 * b) / sr;
    out[i] = 0.8 * Math.sin(p) * Math.exp(-t / 0.07) + 0.3 * Math.sin(p2) * Math.exp(-t / 0.05);
  }
  const hp = new Biquad(sr, "highpass", 1800, 0.7), bp = new Biquad(sr, "bandpass", 4500, 0.7);
  burst(out, sr, 0, 0.75, 0.11, white, { run: (x) => bp.run(hp.run(x)) }, 0.001); // the snares
  burst(out, sr, 0, 0.6, 0.0015, white, new Biquad(sr, "highpass", 3500, 0.7)); // the stick
  return normalize([out], 0.85);
}

// --- crowds and horses (stereo) ---------------------------------------------------------------------------

function hooves(r: Rng, _v: number, sr: number): Float32Array[] {
  const len = 3.8, L = buffer(sr, len), R = buffer(sr, len), white = noise(r), brown = noise(r, "brown");
  const hits = [0, 1, 2, 3].map(() => {
    const h = buffer(sr, 0.1);
    mode(h, sr, 0.0005, between(r, 80, 140), 1, 0.025);
    burst(h, sr, 0, 0.7, 0.012, white, new Biquad(sr, "lowpass", 700, 0.7), 0.0008);
    burst(h, sr, 0, 0.15, 0.0015, white, new Biquad(sr, "highpass", 1500, 0.7));
    return h;
  });
  for (let h = 0; h < 26; h++) { // each horse at a gallop: four footfalls a stride, a moment in the air
    const T = between(r, 0.4, 0.48), g = between(r, 0.35, 1), gains = panGains(between(r, -0.9, 0.9));
    for (let s = -r() * T; s < len; s += T * between(r, 0.97, 1.03)) {
      [0, 0.12, 0.25, 0.34].forEach((o, k) => {
        const at = s + (o + between(r, -0.01, 0.01)) * T;
        if (at >= 0) mix([L, R], hits[(h + k) % 4], sr, at, gains.map((x) => x * g * (k === 2 ? 1 : 0.75) * between(r, 0.7, 1)));
      });
    }
  }
  const lpL = new Biquad(sr, "lowpass", 1200, 0.7), lpR = new Biquad(sr, "lowpass", 1200, 0.7), ground = new Biquad(sr, "lowpass", 120, 0.7);
  for (let i = 0; i < L.length; i++) { // the charge comes closer: louder, brighter, the ground rumbling
    const e = 0.3 + 0.7 * smoothstep(0, 3.2, i / sr);
    if (i % 64 === 0) { lpL.set("lowpass", 1200 + 2000 * e, 0.7); lpR.set("lowpass", 1200 + 2000 * e, 0.7); }
    const g = ground.run(brown()) * 0.5 * e;
    L[i] = lpL.run(L[i]) * e + g;
    R[i] = lpR.run(R[i]) * e + g;
  }
  fadeOut(L, sr, 0.3);
  fadeOut(R, sr, 0.3);
  return normalize([L, R], 0.85);
}

const VOWELS = [[750, 1150, 2500], [560, 900, 2450], [620, 1650, 2500], [680, 1250, 2550]]; // a, o, e, a–o

function roar(r: Rng, _v: number, sr: number): Float32Array[] {
  const len = 3.6, L = buffer(sr, len), R = buffer(sr, len), white = noise(r);
  for (let k = 0; k < 22; k++) { // shouting men, each his own pitch, vowel and breath
    const on = between(r, 0, 0.8), off = Math.min(len - 0.05, on + between(r, 1.6, 2.7));
    const f0 = between(r, 140, 250), F = pick(r, VOWELS).map((x) => x * between(r, 0.92, 1.08));
    const fs = [new Biquad(sr, "bandpass", F[0], F[0] / 90), new Biquad(sr, "bandpass", F[1], F[1] / 120), new Biquad(sr, "bandpass", F[2], F[2] / 170)];
    const osc = new Osc(sr, r()), vib = between(r, 5, 7), [gl, gr] = panGains(between(r, -0.85, 0.85)), amp = between(r, 0.5, 1);
    const i0 = Math.round(on * sr), i1 = Math.round(off * sr), d = (i1 - i0) / sr;
    let jit = 0;
    for (let i = i0; i < i1; i++) {
      const te = (i - i0) / sr;
      jit = (jit + (r() - 0.5) * 0.002) * 0.998;
      const f = f0 * (1 + 0.12 * (1 - Math.exp(-te / 0.25))) * (1 - (0.12 * te) / d) * (1 + 0.02 * Math.sin(TAU * vib * te)) * (1 + jit);
      const src = osc.next(f)[0] * 0.8 + white() * 0.25;
      const e = smoothstep(0, 0.22, te) * smoothstep(0, 0.3, d - te) * (1 + 0.15 * Math.sin(TAU * 4.3 * te + k)) * amp;
      const y = (fs[0].run(src) + fs[1].run(src) * 0.55 + fs[2].run(src) * 0.25) * e;
      L[i] += y * gl;
      R[i] += y * gr;
    }
  }
  const pl = noise(r, "pink"), pr = noise(r, "pink"), bl = new Biquad(sr, "bandpass", 700, 0.8), br = new Biquad(sr, "bandpass", 760, 0.8);
  const ol = new OnePole(sr, 3800), or = new OnePole(sr, 3800);
  for (let i = 0; i < L.length; i++) { // the whole army behind them
    const t = i / sr, e = smoothstep(0, 0.9, t) * smoothstep(0, 0.5, len - t) * 0.5;
    L[i] = ol.run(L[i] + bl.run(pl()) * e);
    R[i] = or.run(R[i] + br.run(pr()) * e);
  }
  return normalize([L, R], 0.8);
}

function oars(r: Rng, _v: number, sr: number): Float32Array[] {
  const len = 1.6, L = buffer(sr, len), R = buffer(sr, len), white = noise(r);
  for (let k = 0; k < 8; k++) { // a bank of oars, pulling together, not quite as one
    const c = buffer(sr, len), t0 = 0.05 + between(r, -0.03, 0.06), dip = new Biquad(sr, "bandpass", 900, 1.1);
    const pull = new Biquad(sr, "lowpass", 700, 0.7), thole = new Biquad(sr, "bandpass", 480, 12);
    for (let i = 0; i < c.length; i++) {
      const t = i / sr, tt = t - t0;
      if (tt >= 0 && tt < 0.2) {
        if (i % 16 === 0) dip.set("bandpass", 900 + 1700 * (tt / 0.2), 1.1);
        c[i] += dip.run(white()) * 0.6 * Math.exp(-tt / 0.05);
      }
      c[i] += pull.run(white()) * 0.25 * hump(t, t0 + 0.35, 0.18) + thole.run(white()) * 0.15 * hump(t, t0 + 0.45, 0.1) * (0.5 + 0.5 * Math.sin(TAU * 30 * t));
    }
    for (let j = 0; j < 3; j++) chirp(c, sr, t0 + between(r, 0, 0.08), 500, 1400, 0.015, 0.15); // bubbles
    mix([L, R], c, sr, 0, panGains(-0.8 + (1.6 * k) / 7 + between(r, -0.1, 0.1)));
  }
  return normalize([L, R], 0.8);
}

// --- sieges, ships, fire ------------------------------------------------------------------------------

function treb(r: Rng, _v: number, sr: number): Float32Array[] {
  const out = buffer(sr, 1.8), white = noise(r);
  for (let k = 0; k < 12; k++) { // the windlass creaks
    burst(out, sr, 0.55 * (1 - (1 - k / 12) ** 1.6), between(r, 0.25, 0.45), 0.003, white,
      new Biquad(sr, "bandpass", between(r, 600, 1100), 8), 0.0005);
  }
  ([[68, 1, 0.15], [112, 0.6, 0.09], [186, 0.4, 0.05], [260, 0.25, 0.04]] as const).forEach(([f, a, tau]) =>
    mode(out, sr, 0.6, f * between(r, 0.95, 1.05), a, tau)); // the arm slams against its stop
  burst(out, sr, 0.6, 0.9, 0.03, white, new Biquad(sr, "lowpass", 600, 0.7), 0.001);
  const bp = new Biquad(sr, "bandpass", 300, 1.2);
  for (let i = Math.round(0.6 * sr); i < Math.round(1.6 * sr); i++) { // the sling swings through
    const t = i / sr;
    if (i % 32 === 0) bp.set("bandpass", 240 + 560 * hump(t, 0.85, 0.2), 1.2);
    out[i] += bp.run(white()) * 0.6 * hump(t, 0.85, 0.22);
  }
  for (let k = 0; k < 4; k++) burst(out, sr, between(r, 0.7, 1), 0.15, 0.002, white, new Biquad(sr, "bandpass", between(r, 900, 1600), 6));
  fadeOut(out, sr, 0.1);
  return normalize([out], 0.85);
}

function stone(r: Rng, _v: number, sr: number): Float32Array[] {
  const out = buffer(sr, 1.8), white = noise(r);
  thump(out, sr, 0.001, between(r, 44, 52), 32, 0.08, 0.15, 1);
  burst(out, sr, 0.001, 1, 0.05, white, new Biquad(sr, "lowpass", 1100, 0.7), 0.001);
  burst(out, sr, 0.001, 0.5, 0.006, white, new Biquad(sr, "highpass", 900, 0.7));
  for (const t of moments(r, 0.03, 1.6, (x) => 70 * Math.exp(-x / 0.45) + 3)) { // rubble coming down
    mode(out, sr, t, between(r, 500, 2500), 0.35 * r() ** 2 * Math.exp(-t / 0.9), between(r, 0.004, 0.015), r() * TAU);
  }
  burst(out, sr, 0.02, 0.06, 0.6, white, new Biquad(sr, "bandpass", 1800, 0.6), 0.05); // dust
  return normalize([out], 0.9);
}

function ram(r: Rng, _v: number, sr: number): Float32Array[] {
  const out = buffer(sr, 1.3), white = noise(r);
  ([[55, 1, 0.4], [87, 0.55, 0.25], [133, 0.35, 0.14], [205, 0.2, 0.08]] as const).forEach(([f, a, tau]) =>
    mode(out, sr, 0.001, f * between(r, 0.95, 1.05), a, tau)); // a gate's timbers
  burst(out, sr, 0, 0.9, 0.05, white, new Biquad(sr, "lowpass", 450, 0.7), 0.001);
  burst(out, sr, 0, 0.45, 0.012, white, new Biquad(sr, "bandpass", 1400, 1));
  for (const f of [1250, 1710, 2230]) mode(out, sr, 0.003, f * between(r, 0.97, 1.03), 0.12, 0.06); // its iron bands
  return normalize([out], 0.9);
}

function splinter(r: Rng, _v: number, sr: number): Float32Array[] {
  const out = buffer(sr, 1), white = noise(r);
  mode(out, sr, 0.001, between(r, 55, 70), 0.8, 0.09);
  burst(out, sr, 0, 0.7, 0.05, white, new Biquad(sr, "lowpass", 400, 0.7), 0.001);
  for (const t of moments(r, 0, 0.8, (x) => 420 * Math.exp(-x / 0.12) + 8)) { // planks splitting
    burst(out, sr, t, 0.7 * r() ** 2, between(r, 0.0003, 0.0015), white, new Biquad(sr, "highpass", 1500, 0.7), 0.0001);
  }
  const bp = new Biquad(sr, "bandpass", 650, 10);
  for (let i = Math.round(0.25 * sr); i < Math.round(0.9 * sr); i++) { // timbers groaning
    const t = i / sr;
    out[i] += bp.run(white()) * 0.2 * hump(t, 0.5, 0.18) * (0.6 + 0.4 * Math.sin(TAU * 23 * t));
  }
  return normalize([out], 0.9);
}

function splash(r: Rng, _v: number, sr: number): Float32Array[] {
  const out = buffer(sr, 1.9), white = noise(r);
  thump(out, sr, 0, 140, 60, 0.03, 0.04, 0.6);
  const bp = new Biquad(sr, "bandpass", 2600, 0.8), lp = new Biquad(sr, "lowpass", 1600, 0.7);
  for (let i = 0; i < out.length; i++) {
    const t = i / sr;
    if (i % 32 === 0) bp.set("bandpass", 900 + 1700 * Math.exp(-t / 0.3), 0.8);
    out[i] += bp.run(white()) * 0.9 * Math.min(1, t / 0.005) * Math.exp(-t / 0.28) + lp.run(white()) * 0.5 * hump(t, 0.75, 0.35, 0.8);
  }
  for (let k = 0; k < 30; k++) { // the column falling back in drops
    const f0 = between(r, 900, 2600);
    chirp(out, sr, between(r, 0.2, 1.6), f0, f0 * 1.6, 0.012, between(r, 0.05, 0.12));
  }
  return normalize([out], 0.85);
}

function fire(r: Rng, _v: number, sr: number): Float32Array[] {
  const len = 4.25, L = buffer(sr, len), R = buffer(sr, len), white = noise(r), bl = noise(r, "brown"), brr = noise(r, "brown");
  const rl = new Biquad(sr, "lowpass", 350, 0.7), rr = new Biquad(sr, "lowpass", 330, 0.7);
  const hl = new Biquad(sr, "highpass", 5000, 0.7), hr = new Biquad(sr, "highpass", 5000, 0.7);
  for (let i = 0; i < L.length; i++) { // the roar of the flames, breathing
    const t = i / sr;
    L[i] = rl.run(bl()) * 0.5 * (0.6 + 0.4 * Math.sin(TAU * 0.37 * t)) + hl.run(white()) * 0.03;
    R[i] = rr.run(brr()) * 0.5 * (0.6 + 0.4 * Math.sin(TAU * 0.29 * t + 1)) + hr.run(white()) * 0.03;
  }
  for (const t of moments(r, 0, len, () => 22)) { // crackles, a few of them snapping
    const c = buffer(sr, 0.03), a = 0.05 + 0.95 * r() ** 3;
    burst(c, sr, 0, a, between(r, 0.0004, 0.003), white, new Biquad(sr, "highpass", 1800, 0.7), 0.0002);
    if (r() < 0.15) mode(c, sr, 0, between(r, 100, 160), a * 0.6, 0.008);
    mix([L, R], c, sr, t, panGains(between(r, -0.8, 0.8)));
  }
  return normalize([loopable(L, sr, 0.25), loopable(R, sr, 0.25)], 0.7);
}

// --- engines ---------------------------------------------------------------------------------------------

function tank(r: Rng, _v: number, sr: number): Float32Array[] {
  const len = 4.25, eng = buffer(sr, len), white = noise(r), brown = noise(r, "brown"), frev = between(r, 27, 33);
  for (let t = 0, k = 0; t < len; t += (1 / (frev * 6)) * between(r, 0.98, 1.02), k++) { // twelve cylinders, unevenly
    const a = (k % 6 === 0 ? 1 : 0.75) * between(r, 0.7, 1);
    mode(eng, sr, t, between(r, 70, 95), a, 0.012);
    mode(eng, sr, t, between(r, 160, 220), a * 0.4, 0.008);
  }
  const ex = new Biquad(sr, "lowpass", 220, 0.7);
  let ph = 0;
  for (let i = 0; i < eng.length; i++) {
    ph += (TAU * frev) / sr;
    eng[i] += Math.sin(ph) * 0.3 + ex.run(brown()) * 0.35;
  }
  const L = eng.slice(), R = eng.slice();
  for (let t = 0; t < len; t += (1 / 9) * between(r, 0.85, 1.15)) { // track links over the sprockets
    const c = buffer(sr, 0.04), f = between(r, 1700, 2400);
    mode(c, sr, 0, f, 0.3, 0.006, r() * TAU);
    mode(c, sr, 0, f * 1.5, 0.15, 0.004);
    burst(c, sr, 0, 0.2, 0.002, white, new Biquad(sr, "highpass", 2000, 0.7));
    mix([L, R], c, sr, t, panGains(r() < 0.5 ? -0.35 : 0.35));
  }
  const sq = new Biquad(sr, "bandpass", 2300, 30);
  for (let i = 0; i < L.length; i++) { // and the odd squeal of the running gear
    const t = i / sr;
    if (i % 64 === 0) sq.set("bandpass", 2300 * (1 + 0.03 * Math.sin(TAU * 0.6 * t)), 30);
    const s = sq.run(white()) * 0.12 * (0.5 + 0.5 * Math.sin(TAU * 0.23 * t + 1.3)) ** 4;
    L[i] += s * 0.8;
    R[i] += s;
  }
  return normalize([loopable(L, sr, 0.25), loopable(R, sr, 0.25)], 0.8);
}

function rotor(r: Rng, _v: number, sr: number): Float32Array[] {
  const len = 2.1, out = buffer(sr, len), white = noise(r);
  for (let k = 0; k / 10.5 < len; k++) { // two blades: 21 slaps in two seconds, one blade a little louder
    const a = k % 2 ? 0.8 : 1;
    burst(out, sr, k / 10.5, 0.9 * a, 0.022, white, new Biquad(sr, "bandpass", between(r, 220, 300), 0.9), 0.002);
    mode(out, sr, k / 10.5, 58, 0.5 * a, 0.03);
  }
  for (let t = 0; t < len; t += 1 / 52.5) mode(out, sr, t, 650, 0.06, 0.003); // the tail rotor
  const lp = new Biquad(sr, "lowpass", 900, 0.7);
  let p1 = 0, p2 = 0;
  for (let i = 0; i < out.length; i++) { // the turbine
    p1 += (TAU * 3100) / sr;
    p2 += (TAU * 4650) / sr;
    out[i] += Math.sin(p1) * 0.02 + Math.sin(p2) * 0.01 + lp.run(white()) * 0.08;
  }
  return normalize([loopable(out, sr, 0.1)], 0.8);
}

function rocket(r: Rng, _v: number, sr: number): Float32Array[] {
  const out = buffer(sr, 1.8), white = noise(r), w2 = noise(r);
  burst(out, sr, 0, 0.8, 0.008, white, new Biquad(sr, "highpass", 400, 0.7)); // the launch
  mode(out, sr, 0.001, 90, 0.6, 0.03);
  const bp = new Biquad(sr, "bandpass", 900, 0.9), rough = new OnePole(sr, 40);
  for (let i = Math.round(0.02 * sr); i < out.length; i++) { // the motor's roar, going away
    const t = i / sr;
    if (i % 32 === 0) bp.set("bandpass", t < 0.25 ? 900 + (1700 * t) / 0.25 : 2600 - 800 * Math.min(1, (t - 0.25) / 1.4), 0.9);
    const e = smoothstep(0.02, 0.05, t) * (t < 0.5 ? 1 : Math.exp(-(t - 0.5) / 0.5));
    out[i] += bp.run(white()) * 0.7 * e * (1 + 8 * rough.run(w2()));
  }
  return normalize([out], 0.85);
}

export type SampleId =
  | "sword" | "shield" | "bow" | "arrow" | "thud" | "horn" | "bugle" | "wardrum" | "snare" | "hooves" | "roar" | "oars"
  | "treb" | "stone" | "ram" | "splinter" | "splash" | "fire" | "musket" | "cannon" | "rifle" | "mg" | "ar" | "hmg"
  | "blast" | "tank" | "rotor" | "rocket";

export const RECIPES: Record<SampleId, Recipe> = {
  sword: { sr: 24000, variants: 5, render: sword },
  shield: { sr: 22050, variants: 4, render: shield },
  bow: { sr: 22050, variants: 3, render: bow },
  arrow: { sr: 24000, variants: 4, render: arrow },
  thud: { sr: 24000, variants: 6, render: thud },
  horn: { sr: 32000, variants: 3, render: horn },
  bugle: { sr: 32000, variants: 2, render: bugle },
  wardrum: { sr: 22050, variants: 3, render: wardrum },
  snare: { sr: 32000, variants: 4, render: snare },
  hooves: { sr: 16000, variants: 2, render: hooves },
  roar: { sr: 16000, variants: 2, heavy: true, render: roar },
  oars: { sr: 16000, variants: 2, heavy: true, render: oars },
  treb: { sr: 22050, variants: 2, render: treb },
  stone: { sr: 22050, variants: 3, render: stone },
  ram: { sr: 22050, variants: 2, render: ram },
  splinter: { sr: 24000, variants: 3, render: splinter },
  splash: { sr: 22050, variants: 3, render: splash },
  fire: { sr: 22050, variants: 1, loop: true, render: fire },
  // black powder: a deep boom, little crack, the flintlock's flash in the pan
  musket: { sr: 32000, variants: 5, render: shot({ len: 0.9, crack: 0, lead: [0, 0], tau: 0.032, bp: [700, 1100], q: 0.6, lp: 2800,
    body: [72, 0.9, 0.07], tail: [0.4, 0.2], echo: 0.35, flash: 0.35 }) },
  cannon: { sr: 22050, variants: 3, render: cannon },
  // smokeless rifles: the bullet's crack, then a sharper report
  rifle: { sr: 32000, variants: 5, render: shot({ len: 0.8, crack: 0.55, lead: [0.03, 0.09], tau: 0.016, bp: [1300, 1900], q: 0.7,
    lp: 5200, body: [115, 0.55, 0.035], tail: [0.25, 0.14], echo: 0.3 }) },
  mg: { sr: 32000, variants: 4, render: shot({ len: 0.3, crack: 0.4, lead: [0.015, 0.03], tau: 0.011, bp: [1500, 2000], q: 0.8,
    lp: 5200, body: [125, 0.5, 0.024], tail: [0.15, 0.07], echo: 0.2, clank: 0.16 }) },
  ar: { sr: 32000, variants: 5, render: shot({ len: 0.38, crack: 0.5, lead: [0.01, 0.03], tau: 0.01, bp: [1700, 2900], q: 0.65,
    lp: 7000, body: [140, 0.45, 0.022], tail: [0.18, 0.09], echo: 0.25 }) },
  hmg: { sr: 32000, variants: 3, render: shot({ len: 0.5, crack: 0.5, lead: [0.02, 0.05], tau: 0.02, bp: [900, 1300], q: 0.7,
    lp: 4200, body: [78, 1, 0.05], tail: [0.32, 0.15], echo: 0.3, clank: 0.1 }) },
  blast: { sr: 22050, variants: 3, render: blast },
  tank: { sr: 22050, variants: 1, loop: true, render: tank },
  rotor: { sr: 22050, variants: 1, loop: true, render: rotor },
  rocket: { sr: 32000, variants: 2, render: rocket },
};

/** Render variant v of a sound (seeded by its name and variant: the same in every browser). Every sample that does not
 * loop ends in silence (no click when it stops). */
export function renderSample(id: SampleId, v: number): { sr: number; channels: Float32Array[] } {
  const rec = RECIPES[id];
  let seed = 7;
  for (const ch of id) seed = (seed * 31 + ch.charCodeAt(0)) >>> 0;
  const channels = rec.render(rng(seed + v * 7919), v, rec.sr);
  if (!rec.loop) for (const c of channels) fadeOut(c, rec.sr, 0.06);
  return { sr: rec.sr, channels };
}
